import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import '../styles/EntityManagement.css';
import {
  Alert,
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Snackbar,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Divider,
  CircularProgress } from
'@mui/material';
import Grid from '@mui/material/Grid';
import EntityGroupTable from '../components/EntityManagement/EntityGroupTable';
import api from '../services/api';
import EntityGroupDialog from '../components/EntityManagement/EntityGroupDialog';
import { usePointInTimeFilter } from '../hooks/usePointInTimeFilter';

const EntityGroupsManagement = () => {
  // Get point-in-time filter context
  const { selectedPointInTime, availablePointInTimes } = usePointInTimeFilter();

  // Get PointInTime name for display
  const getPointInTimeName = (pointInTimeCode) => {
    if (!pointInTimeCode) return '';
    const pointInTimeObj = availablePointInTimes.find((pit) => pit.code === pointInTimeCode);
    return pointInTimeObj ? pointInTimeObj.name : pointInTimeCode;
  };

  // Columns for entity groups
  const groupColumns = [
  { field: 'name', headerName: 'NAME', width: 150,
    renderCell: (params) =>
    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'var(--color-black)' }}>
          {params.value}
        </Typography>

  },
  { field: 'description', headerName: 'DESCRIPTION', width: 250,
    renderCell: (params) =>
    <Typography variant="body2" sx={{ textAlign: 'left', width: '100%', lineHeight: '1.5', paddingY: '4px' }}>
          {params.value}
        </Typography>

  },
  { field: 'type', headerName: 'TYPE', width: 150,
    renderCell: (params) =>
    <Typography variant="body2" sx={{ textAlign: 'left', width: '100%' }}>
          {params.value}
        </Typography>

  },
  { field: 'groupType', headerName: 'GROUP TYPE', width: 150,
    renderCell: (params) =>
    <Typography variant="body2" sx={{ textAlign: 'left', width: '100%' }}>
          {params.value}
        </Typography>

  },
  { field: 'level', headerName: 'LEVEL', width: 100, type: 'number',
    renderCell: (params) =>
    <Typography variant="body2" sx={{ textAlign: 'left', width: '100%' }}>
          {params.value}
        </Typography>

  },
  { field: 'parent', headerName: 'PARENT GROUP', width: 200,
    renderCell: (params) =>
    <Typography variant="body2" sx={{ textAlign: 'left', width: '100%' }}>
          {params.value}
        </Typography>

  },
  { field: 'entityCount', headerName: 'ENTITIES', width: 100, type: 'number',
    renderCell: (params) =>
    <Typography variant="body2" sx={{ textAlign: 'left', width: '100%' }}>
          {params.value}
        </Typography>

  },
  {
    field: 'pointInTime',
    headerName: 'POINT IN TIME',
    width: 150,
    renderCell: (params) => {
      const pointInTimeCode = params.row.PointInTime;
      const pointInTimeName = getPointInTimeName(pointInTimeCode);

      return (
        <Typography variant="body2" sx={{ textAlign: 'left', width: '100%' }}>
            {pointInTimeName}
          </Typography>);

    }
  },
  {
    field: 'actions',
    headerName: 'Actions',
    width: 100,
    renderCell: (params) =>
    <Box>
          <IconButton size="small" onClick={() => handleEditGroup(params.row)} title="Edit Group">
            <img src="/assets/Icons/icon_pencil.svg" alt="Edit" style={{ width: 18, height: 18 }} />
          </IconButton>
          <IconButton size="small" onClick={() => handleDeleteGroup(params.row.id)} title="Delete Group">
            <img src="/assets/Icons/icon_trash.svg" alt="Delete" style={{ width: 18, height: 18 }} />
          </IconButton>
        </Box>

  }];


  const [entityGroups, setEntityGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedGroupRows, setSelectedGroupRows] = useState([]);
  const [openGroupDialog, setOpenGroupDialog] = useState(false);
  const [currentGroup, setCurrentGroup] = useState({
    name: '',
    description: '',
    type: 'user_defined',
    groupType: 'custom',
    level: 1,
    parent: null,
    parentId: null,
    PointInTime: selectedPointInTime !== 'All' ? selectedPointInTime : availablePointInTimes[0]?.code || '',
    entityList: [],
    _newEntityId: '',
    entityIdError: ''
  });
  const [entities, setEntities] = useState([]);
  const [entityLoading, setEntityLoading] = useState(false);

  // Fetch master entities from API
  const fetchEntities = async () => {
    setEntityLoading(true);
    try {
      const response = await api.get('/entity-models/master');
      if (response.data && response.data.data) {
        setEntities(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching entities:', error);
      setNotification({
        open: true,
        message: 'Failed to load entities',
        severity: 'error'
      });
    } finally {
      setEntityLoading(false);
    }
  };

  // Look up entity name based on entity ID
  const lookupEntityName = (entityId) => {
    if (!entityId) {
      return '';
    }
    const entity = entities.find((e) => e.id === entityId);
    return entity ? entity.EntityName || '' : '';
  };

  const [editGroupMode, setEditGroupMode] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: ''
  });

  const isDescendantOfCurrentGroup = useCallback((group) => {
    if (!currentGroup.id) return false;

    const visitedGroupIds = new Set();
    let parentId = group.parentId;

    while (parentId) {
      if (parentId === currentGroup.id) {
        return true;
      }

      if (visitedGroupIds.has(parentId)) {
        return false;
      }

      visitedGroupIds.add(parentId);
      const parentGroup = entityGroups.find((candidateGroup) => candidateGroup.id === parentId);
      parentId = parentGroup?.parentId;
    }

    return false;
  }, [currentGroup.id, entityGroups]);

  const isValidParentForLevel = useCallback((parentId, level) => {
    if (!parentId) return false;
    const parentGroup = entityGroups.find((group) => group.id === parentId);
    return parentGroup &&
      Number(parentGroup.level) === Number(level) - 1 &&
      !isDescendantOfCurrentGroup(parentGroup);
  }, [entityGroups, isDescendantOfCurrentGroup]);

  // Handle input changes in group form
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setCurrentGroup((prev) => {
      if (name === 'level') {
        const nextLevel = Number(value);
        const shouldClearParent = nextLevel === 1 || !isValidParentForLevel(prev.parentId, nextLevel);

        return {
          ...prev,
          level: value,
          parentId: shouldClearParent ? null : prev.parentId
        };
      }

      return {
        ...prev,
        [name]: name === 'parentId' ? value || null : value
      };
    });

    // Reset error state when input changes
    if (name === '_newEntityId') {
      setCurrentGroup((prev) => ({
        ...prev,
        entityIdError: ''
      }));
    }
  };
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(true);
  // Check if all required fields are filled
  useEffect(() => {
    const requiredFields = [
    'name',
    'type',
    'groupType',
    'level'];


    const allFieldsFilled = requiredFields.every((field) =>
    currentGroup[field] !== undefined && currentGroup[field] !== ''
    );
    const level = Number(currentGroup.level);
    const levelIsValid = Number.isInteger(level) && level >= 1;
    const parentRequired = level > 1;
    const parentIsValid = parentRequired ? isValidParentForLevel(currentGroup.parentId, level) : true;

    setSaveButtonDisabled(!allFieldsFilled || !levelIsValid || !parentIsValid);
  }, [currentGroup, isValidParentForLevel]);

  // Fetch data when component mounts or point-in-time changes
  useEffect(() => {
    fetchEntityGroups();
    fetchEntities();
  }, [selectedPointInTime]);

  const handleNotificationClose = () => {
    setNotification({
      open: false,
      message: '',
      severity: ''
    });
  };

  // Fetch entity groups from API with point-in-time filtering
  const fetchEntityGroups = async () => {
    try {
      setLoading(true);

      // First try to fetch without point-in-time parameter to avoid API issues
      let url = '/entity-groups';
      let response;

      // Always fetch all groups and filter client-side for now
      // This ensures we can debug the data structure and filtering logic

      response = await api.get(url);

      // TODO: Re-enable server-side filtering once backend is confirmed working
      // if (selectedPointInTime && selectedPointInTime !== 'All') {
      //   const pointInTimeUrl = `${url}?pointInTime=${encodeURIComponent(selectedPointInTime)}`;
      //   response = await api.get(pointInTimeUrl);
      // }

      // Normalize the data to match our expected structure
      const normalizedGroups = (response.data.data || []).map((group) => {
        // Handle both new PointInTime format and legacy Year/Period format
        let pointInTime = group.PointInTime;
        if (!pointInTime && group.Year && group.Period) {
          // Fallback: construct PointInTime from Year and Period for legacy data
          pointInTime = `${group.Year}-${group.Period}`;
        }

        return {
          ...group,
          PointInTime: pointInTime, // Ensure PointInTime is always available
          parent: group.parent ? group.parent.name : null,
          parentId: group.parent ? group.parent.id : null,
          entityList: group.entityList || []
        };
      });





      // If we have a specific point-in-time selected and got all groups, filter them client-side
      let filteredGroups = normalizedGroups;
      if (selectedPointInTime && selectedPointInTime !== 'All') {
        filteredGroups = normalizedGroups.filter((group) => {
          const matches = group.PointInTime === selectedPointInTime;
          if (!matches && normalizedGroups.indexOf(group) < 3) {

          }
          return matches;
        });

      }

      setEntityGroups(filteredGroups);
    } catch (error) {
      console.error('Error fetching entity groups:', error);
      setNotification({
        open: true,
        message: `Failed to fetch entity groups: ${error.message || 'Unknown error'}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Delete a single group
  const handleDeleteGroup = async (id) => {
    if (window.confirm('Are you sure you want to delete this group?')) {
      try {
        await api.delete(`/entity-groups/${id}`);
        await fetchEntityGroups();
        setNotification({
          open: true,
          message: 'Group deleted successfully',
          severity: 'success'
        });
      } catch (error) {
        console.error('Error deleting group:', error);
        setNotification({
          open: true,
          message: 'Failed to delete group',
          severity: 'error'
        });
      }
    }
  };

  // Delete multiple selected groups
  const handleDeleteSelectedGroups = async () => {
    if (selectedGroupRows.length === 0) return;

    if (window.confirm(`Are you sure you want to delete ${selectedGroupRows.length} selected groups?`)) {
      setDeleting(true);
      try {
        const deletePromises = selectedGroupRows.map((id) => api.delete(`/entity-groups/${id}`));
        await Promise.all(deletePromises);
        await fetchEntityGroups();
        setSelectedGroupRows([]);
        setNotification({
          open: true,
          message: `Successfully deleted ${selectedGroupRows.length} groups`,
          severity: 'success'
        });
      } catch (error) {
        console.error('Error deleting groups:', error);
        setNotification({
          open: true,
          message: 'Failed to delete groups',
          severity: 'error'
        });
      } finally {
        setDeleting(false);
      }
    }
  };

  // Handle group selection change
  const handleGroupSelectionChange = (newSelection) => {
    setSelectedGroupRows(newSelection);
  };

  // Handle edit group
  const handleEditGroup = (group) => {
    // Ensure entityList is an array
    const normalizedGroup = {
      ...group,
      entityList: Array.isArray(group.entityList) ? group.entityList : [],
      _newEntityId: '',
      entityIdError: ''
    };
    setCurrentGroup(normalizedGroup);
    setEditGroupMode(true);
    setOpenGroupDialog(true);
  };

  // Handle adding new group
  const handleAddNewGroup = () => {
    setCurrentGroup({
      name: '',
      description: '',
      type: 'user_defined',
      groupType: 'custom',
      level: 1,
      parent: null,
      parentId: null,
      PointInTime: selectedPointInTime !== 'All' ? selectedPointInTime : availablePointInTimes[0]?.code || '',
      ModifiedBy: 'admin',
      entityList: [],
      _newEntityId: '',
      entityIdError: ''
    });
    setEditGroupMode(false);
    setOpenGroupDialog(true);
  };

  // Handle closing group dialog
  const handleCloseGroup = () => {
    setOpenGroupDialog(false);

    setCurrentGroup({
      name: '',
      description: '',
      type: 'user_defined',
      groupType: 'custom',
      level: 1,
      parent: null,
      parentId: null,
      PointInTime: selectedPointInTime !== 'All' ? selectedPointInTime : availablePointInTimes[0]?.code || '',
      entityList: [],
      _newEntityId: '',
      entityIdError: ''
    });
  };

  // Handle saving a group
  const handleSaveGroup = async () => {
    try {
      // Validate required fields
      const requiredFields = [
      { field: 'name', label: 'Group Name' },
      { field: 'type', label: 'Type' },
      { field: 'groupType', label: 'Group Type' },
      { field: 'level', label: 'Level' }];


      const missingField = requiredFields.find((field) =>
      !currentGroup[field.field] || currentGroup[field.field] === ''
      );

      if (missingField) {
        setNotification({
          open: true,
          message: `Please fill in the required field: ${missingField.label}`,
          severity: 'error'
        });
        return;
      }

      const level = Number(currentGroup.level);

      if (!Number.isInteger(level) || level < 1) {
        setNotification({
          open: true,
          message: 'Level must be a whole number greater than or equal to 1',
          severity: 'error'
        });
        return;
      }

      const parentId = level === 1 ? null : currentGroup.parentId || null;

      if (level > 1 && !parentId) {
        setNotification({
          open: true,
          message: 'Please select a parent group',
          severity: 'error'
        });
        return;
      }

      if (level > 1 && !isValidParentForLevel(parentId, level)) {
        setNotification({
          open: true,
          message: `Level ${level} groups must have a level ${level - 1} parent`,
          severity: 'error'
        });
        return;
      }

      // Prepare data for API
      const groupData = {
        name: currentGroup.name,
        description: currentGroup.description,
        type: currentGroup.type,
        groupType: currentGroup.groupType,
        level,
        parentId,
        PointInTime: currentGroup.PointInTime,
        entityList: currentGroup.entityList
      };

      if (editGroupMode) {
        // For updates, check if we're updating for a different point-in-time
        const isPointInTimeUpdate = selectedPointInTime && selectedPointInTime !== 'All' &&
        currentGroup.PointInTime !== groupData.PointInTime;

        // Update existing group
        let url = `/entity-groups/${currentGroup.id}`;
        if (isPointInTimeUpdate) {
          url += '?pointInTime=true';
        }

        await api.put(url, groupData);

        // Handle entity assignments for existing groups
        if (currentGroup.id && groupData.entityList) {
          try {
            // Get current entity assignments for the group
            const currentAssignmentsResponse = await api.get(`/entity-groups/${currentGroup.id}/entities`);
            const currentEntityIds = (currentAssignmentsResponse.data.data || []).map((entity) => entity.id);

            // Find entities to add and remove
            const entitiesToAdd = groupData.entityList.filter((entityId) => !currentEntityIds.includes(entityId));
            const entitiesToRemove = currentEntityIds.filter((entityId) => !groupData.entityList.includes(entityId));

            // Add new entities
            if (entitiesToAdd.length > 0) {
              const addPromises = entitiesToAdd.map((entityId) =>
              api.post(`/entity-groups/${currentGroup.id}/entities/${entityId}`, {
                PointInTime: groupData.PointInTime
              })
              );
              await Promise.all(addPromises);
            }

            // Remove entities
            if (entitiesToRemove.length > 0) {
              const removePromises = entitiesToRemove.map((entityId) =>
              api.delete(`/entity-groups/${currentGroup.id}/entities/${entityId}`)
              );
              await Promise.all(removePromises);
            }
          } catch (assignmentError) {
            console.error('Error updating entity assignments:', assignmentError);
            setNotification({
              open: true,
              message: 'Group updated but some entity assignment changes failed',
              severity: 'warning'
            });
          }
        }

        // Refresh the entity groups list
        await fetchEntityGroups();

        setNotification({
          open: true,
          message: isPointInTimeUpdate ?
          'Entity group updated for new point-in-time successfully' :
          'Entity group updated successfully',
          severity: 'success'
        });
      } else {
        // Create new group
        const response = await api.post('/entity-groups', groupData);
        const newGroupId = response.data.data?.id;

        // If there are entities to assign, assign them after group creation
        if (newGroupId && groupData.entityList && groupData.entityList.length > 0) {
          try {
            // Assign entities to the new group
            const assignmentPromises = groupData.entityList.map((entityId) =>
            api.post(`/entity-groups/${newGroupId}/entities/${entityId}`, {
              PointInTime: groupData.PointInTime
            })
            );
            await Promise.all(assignmentPromises);
          } catch (assignmentError) {
            console.error('Error assigning entities to group:', assignmentError);
            setNotification({
              open: true,
              message: 'Group created but some entity assignments failed',
              severity: 'warning'
            });
          }
        }

        // Refresh the entity groups list
        await fetchEntityGroups();

        setNotification({
          open: true,
          message: 'Entity group created successfully',
          severity: 'success'
        });
      }
      setOpenGroupDialog(false);
    } catch (error) {
      console.error('Error saving entity group:', error);
      setNotification({
        open: true,
        message: 'Failed to save entity group',
        severity: 'error'
      });
    }
  };

  useEffect(() => {
    fetchEntityGroups();
  }, []);

  return (
    <Box className="entity-groups-container">
      <EntityGroupTable
        entityGroups={entityGroups}
        columns={groupColumns}
        loading={loading}
        selectedRows={selectedGroupRows}
        onSelectionChange={handleGroupSelectionChange}
        onAddNew={handleAddNewGroup}
        onDeleteSelected={handleDeleteSelectedGroups}
        onRefresh={fetchEntityGroups}
        deleting={deleting} />

      <EntityGroupDialog
        open={openGroupDialog}
        handleClose={handleCloseGroup}
        handleSave={handleSaveGroup}
        currentGroup={currentGroup}
        editGroupMode={editGroupMode}
        handleInputChange={handleInputChange}
        entityGroups={entityGroups}
        saveButtonDisabled={saveButtonDisabled} />

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>

        <Alert
          onClose={handleNotificationClose}
          severity={notification.severity}
          sx={{ width: '100%' }}>

          {notification.message}
        </Alert>
      </Snackbar>
    </Box>);

};

export default EntityGroupsManagement;
